/**
 * Copy for the public demo site (/demo, /demo/services, /demo/about,
 * /demo/contact, /demo/booking).
 *
 * The demo is a sales tool: prospective customers look round it to see what
 * their own site would be. So instead of copy written for a cleaning company's
 * customers, the section intros, taglines and About text here tell the visitor
 * what *they* would put in each spot and how they'd do it.
 *
 * Only reference things the product really does:
 *  - the sign-up wizard sets business name, web address, service area, first
 *    service and theme (src/pages/Signup.tsx)
 *  - everything else is edited from the admin (homepage editor, About editor,
 *    Services, Team, Reviews incl. Google reviews, Company info, Hours)
 *
 * Sample services, prices and stats stay realistic so the booking flow still
 * demos well. Applied only on /demo routes (see useDemoShowcase); real tenant
 * sites never see this file.
 */
import type { HeroBadge } from "@/hooks/useHeroContent";
import type { AboutMilestone, AboutTeamMember } from "@/hooks/useAboutContent";
import type { ReviewItem } from "@/hooks/useCustomerReviews";

export const DEMO_BUSINESS_NAME = "Your Business Name";

export const DEMO_SHOWCASE = {
  businessName: DEMO_BUSINESS_NAME,

  hero: {
    eyebrow: "Your website, ready to book",
    title: "Your headline goes here",
    subtitle:
      "Tell people what you clean and where. Add a line about why they should pick you, swap in your own photo, and it's live as soon as you save it in your admin.",
    ctaText: "Try the booking form",
    ratingLine: "Worked out from the reviews you approve",
    badges: [
      { icon: "Shield", title: "Your selling points", subtitle: "Insured? Vetted? Say so here" },
      { icon: "ThumbsUp", title: "Your promise", subtitle: "The guarantee you give customers" },
      { icon: "Clock", title: "Your hours", subtitle: "When customers can book you" },
    ] satisfies HeroBadge[],
  },

  homeServices: {
    title: "Your services",
    lead:
      "Showcase your services here, with prices and how long each clean takes. Your first one goes in while you sign up, and you can add the rest from your admin in a few minutes.",
  },

  howItWorks: {
    eyebrow: "How booking works",
    title: "How your customers book",
    lead:
      "Customers pick a service, see the price upfront and choose a time that fits your working hours. No back-and-forth messages to arrange it.",
  },

  whyChoose: {
    title: "Why customers should choose you",
    description:
      "Put your best numbers here, like how quickly you reply or how long you've been going. Then list the reasons people pick you over the next company. You can change every word from your admin.",
  },

  homeResults: {
    title: "Show off your best work",
    description:
      "Upload your own before-and-after photos and they appear here and on your About page.",
  },

  testimonials: {
    subtitle:
      "Reviews you approve show up here. You can add testimonials yourself or connect your Google reviews.",
    reviews: [
      {
        name: "James M.",
        location: "Cardiff Bay",
        content:
          "We use them for all five of our Airbnb flats. Turnarounds are quick, the standard never slips, and guests keep mentioning how spotless everything is.",
        rating: 5,
      },
      {
        name: "Emma W.",
        location: "Newport City Centre",
        content:
          "Easy to book online and the cleaner arrived right on time. My kitchen and bathrooms have never looked better. Will definitely be booking regularly!",
        rating: 5,
      },
    ] satisfies ReviewItem[],
  },

  cta: {
    title: "Ready for a spotless home?",
    subtitle:
      "Every page ends with a nudge to book, plus an offer if you run one. Press the button to see the booking form your customers get.",
    checklist: ["Your service areas", "Bookings any time of day", "Your guarantee here"],
  },

  servicesPage: {
    title: "Your service catalogue",
    lead:
      "Showcase your service catalogue here: prices, how long each clean takes and what's included. Your first service is set up in the sign-up wizard, and you can add the rest from your admin in a few minutes.",
    ctaEyebrow: "Not ready to book?",
    ctaTitle: "Give customers another way in",
    ctaSubtitle:
      "Some people want to ask a question first. Callback and quote requests come straight through to your admin.",
  },

  about: {
    eyebrow: "About your business",
    heroTitle: "Your story goes here",
    intro:
      "Tell customers who you are, how you started and what you care about. People like to know who's coming into their home, so this page does a lot of the trust-building for you. You write it in the About editor in your admin.",
    journeyTitle: "Your journey",
    milestones: [
      { year: "2019", event: "Add the moments that matter: when you started, your first big contract, your first hire." },
      { year: "2022", event: "Each milestone is one line in your About editor. Add as many or as few as you like." },
      { year: "Today", event: "Your own booking website goes live with The Polished Version." },
    ] satisfies AboutMilestone[],
    valuesTitle: "What you stand for",
    valuesLead: "Four short values customers can take in at a glance. Choose your own words and icons.",
    teamTitle: "Meet the team",
    teamLead: "Put faces to the name. People you add on your Team page can show up here too, with a photo and a short bio.",
    team: [
      { name: "Your name", role: "Owner", bio: "A few lines about you: why you started, what you're proud of, maybe something you're known for.", initials: "YN" },
      { name: "Team member", role: "Cleaner", bio: "Add the people customers will meet, so they know who to expect at the door.", initials: "TM" },
      { name: "Team member", role: "Office & bookings", bio: "Whoever answers the phone and keeps the diary running. Customers like knowing who they're talking to.", initials: "TM" },
    ] satisfies AboutTeamMember[],
    resultsTitle: "Your before and afters",
    resultsDescription: "Upload photos of your best work. They show here and on your homepage.",
    ctaTitle: "Join the many households and businesses choosing a cleaner, easier way to manage their space",
    ctaSubtitle: "Every page can end with a call to action like this. You choose the wording.",
  },

  contact: {
    lead:
      "Customers message you from here. Each message lands in your admin inbox and you get an email too, so nothing slips through.",
  },

  booking: {
    lead:
      "This is the booking form your customers use. They pick a service, see the price and choose a time that fits your hours.",
    helpText:
      "Not everyone is ready to book straight away. The callback button lets them ask for a call instead, and the request lands in your admin.",
  },
};

export type DemoShowcase = typeof DEMO_SHOWCASE;
